import { useEffect, useState } from 'react';
import { trackEvent } from './useAnalytics';

/**
 * Deterministic A/B variant assignment by sessionId so a returning user
 * sees the same variant. Variant is persisted in sessionStorage and an
 * `experiment_view` event fires once per session so the founder analytics
 * dashboard can compare conversion across variants.
 */
const KEY_PREFIX = 'mbap_exp_';

const hash32 = (s) => {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};

const getSessionId = () => {
  try {
    let id = sessionStorage.getItem('mbap_session_id');
    if (!id) {
      id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem('mbap_session_id', id);
    }
    return id;
  } catch {
    return 'anon';
  }
};

/**
 * useExperiment(name, variants) → variant string
 *
 * Example:
 *   const variant = useExperiment('hero_v1', ['control', 'urgent']);
 *
 * - First call assigns the variant deterministically from the session id.
 * - The chosen variant is stored in sessionStorage and re-read on subsequent
 *   renders to avoid flicker.
 * - Fires an `experiment_view` analytics event once per session/experiment.
 */
export const useExperiment = (name, variants = ['control', 'variant']) => {
  const storageKey = `${KEY_PREFIX}${name}`;
  const [variant, setVariant] = useState(() => {
    try {
      const cached = sessionStorage.getItem(storageKey);
      if (cached && variants.includes(cached)) return cached;
    } catch { /* ignore */ }
    const sid = getSessionId();
    const idx = hash32(`${name}:${sid}`) % variants.length;
    const chosen = variants[idx];
    try { sessionStorage.setItem(storageKey, chosen); } catch { /* ignore */ }
    return chosen;
  });

  useEffect(() => {
    const seenKey = `${storageKey}_logged`;
    try {
      if (sessionStorage.getItem(seenKey)) return;
      sessionStorage.setItem(seenKey, '1');
    } catch { /* ignore */ }
    trackEvent('experiment_view', { label: `${name}:${variant}` });
  }, [name, variant, storageKey]);

  return variant;
};

export default useExperiment;
